import {
  isResolvableUploadImageUrl,
  resolveUploadImageUrl,
} from 'api/utils/resolveUploadImageUrl'
import {
  CreateAdFlowForm,
  initialCreateAdFlowForm,
} from 'components/createAd/createAdFlowState'
import { normalizeListingPhotos } from 'components/createAd/listingPhotos'

const STORAGE_KEY = 'easybail.createAd.draft'

export type CreateAdDraftSnapshot = {
  form: CreateAdFlowForm
  step: number
  highestCompletedStep: number
  furthestStepReached: number
}

function normalizeDraftPhotos(form: CreateAdFlowForm): CreateAdFlowForm['listingPhotos'] {
  return normalizeListingPhotos(form.listingPhotos)
    .filter(photo => isResolvableUploadImageUrl(photo.url))
    .map(photo => ({
      ...photo,
      url: resolveUploadImageUrl(photo.url),
    }))
    .filter(photo => photo.url !== '')
}

function sanitizeFormForStorage(form: CreateAdFlowForm): CreateAdFlowForm {
  return {
    ...form,
    listingPhotos: normalizeDraftPhotos(form),
  }
}

function reviveDraftForm(raw: unknown): CreateAdFlowForm {
  const base = initialCreateAdFlowForm()
  if (!raw || typeof raw !== 'object') return base

  const partial = raw as Partial<CreateAdFlowForm>
  return {
    ...base,
    ...partial,
    services: { ...base.services, ...(partial.services ?? {}) },
    amenities: { ...base.amenities, ...(partial.amenities ?? {}) },
    listingPhotos: normalizeDraftPhotos({
      ...base,
      listingPhotos: normalizeListingPhotos(partial.listingPhotos),
    }),
  }
}

function clampStep(value: unknown, max: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : 0
  return Math.min(Math.max(n, 0), max)
}

export function getCreateAdDraft(maxStep = 4): CreateAdDraftSnapshot | null {
  if (typeof sessionStorage === 'undefined') return null

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<CreateAdDraftSnapshot>
    if (!parsed || typeof parsed !== 'object' || !parsed.form) return null

    return {
      form: reviveDraftForm(parsed.form),
      step: clampStep(parsed.step, maxStep),
      highestCompletedStep: clampStep(parsed.highestCompletedStep, maxStep),
      furthestStepReached: clampStep(parsed.furthestStepReached, maxStep),
    }
  } catch {
    return null
  }
}

export function setCreateAdDraft(snapshot: CreateAdDraftSnapshot): void {
  if (typeof sessionStorage === 'undefined') return

  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...snapshot,
        form: sanitizeFormForStorage(snapshot.form),
      })
    )
  } catch {
    // Ignore quota or private-mode errors.
  }
}

export function clearCreateAdDraft(): void {
  if (typeof sessionStorage === 'undefined') return

  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage errors.
  }
}
